<template>
  <div id="coachSelection">
    <CoachSelectionExplanation />

    <div class="filter">
      <el-select
        v-model="filterSelect"
        :placeholder="$t('languages.language')"
        @change="getCoaches"
      >
        <el-option
          v-for="item in filterOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
    </div>

    <div class="mainCard clear-card coachList">
      <el-table
        v-loading="loading"
        :data="coaches"
        :span-method="spanMethod"
        style="width: 100%;"
        class="clear-table"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileTable' : 'deskTable'"
        :empty-text="noDataText"
      >
        <el-table-column
          :label="$t('leader.coach.coach')"
          prop="coach"
          :width="['mobile', 'tablet'].includes($mq) ? '90' : ''"
        >
          <template slot-scope="scope">
            <el-row class="coachRow">
              <el-col
                :span="responsivePicture.one"
                class="pictureCol"
                :class="['mobile', 'tablet'].includes($mq) ? 'mobilePicture' : ''"
              >
                <el-image
                  :src="scope.row.profile_image"
                  fit="cover"
                >
                  <div
                    slot="placeholder"
                    v-loading="true"
                    class="image-slot"
                  >
                    {{ $t('leader.coach.loading') }}
                  </div>
                  <div
                    slot="error"
                    class="image-slot imageAlt"
                  >
                    <img
                      src="@/assets/coach_avatar/default_profile.jpg"
                      alt="profile"
                      class="defaultProfile"
                    />
                    <!--<i class="el-icon-picture-outline"></i>-->
                  </div>
                </el-image>
              </el-col>
              <el-col
                :span="responsivePicture.two"
                class="infoCol"
              >
                <div class="info">
                  <div
                    v-if="!['mobile', 'tablet'].includes($mq)"
                    class="name defaultCursor"
                  >
                    {{ scope.row.first_name + ' ' + scope.row.last_name }}
                  </div>
                  <div
                    class="infoLink"
                    :class="['mobile', 'tablet'].includes($mq)
                      ? 'mobileModeInfoLink' : 'deskModeInfoLink'"
                  >
                    <el-button
                      type="text"
                      @click="openCoachInfo(scope.row)"
                    >
                      {{ ['desktop', 'laptop'].includes($mq) ? 'View info' : '' }}
                    </el-button>
                  </div>
                </div>
              </el-col>
            </el-row>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('leader.coach.meetingSchedule')"
          prop="schedule"
        >
          <template slot-scope="scope">
            <div class="schedulerLayout">
              <div class="left">
                <div
                  v-if="['mobile', 'tablet'].includes($mq)"
                  class="mobileName"
                >
                  {{ scope.row.first_name + ' ' + scope.row.last_name }}
                </div>
                <CoachScheduler
                  :coach-id="scope.row.uuid"
                  :coach-name="`${scope.row.first_name} ${scope.row.last_name}`"
                  :meeting-time.sync="scope.row.meeting_schedule"
                  :meeting-status.sync="scope.row.meeting_status"
                  :meeting-uuid.sync="scope.row.meeting_uuid"
                  :disabled="scope.row.meeting_status === 'completed'"
                  :placeholder="$t('leader.coach.selectDayTime')"
                />
                <div
                  v-if="['mobile', 'tablet'].includes($mq)"
                  class="mobileStatus"
                >
                  {{ scope.row.meeting_status }}
                </div>
              </div>
              <div class="right">
                <el-button
                  v-if="
                    scope.row.meeting_schedule
                      && scope.row.meeting_status !== 'cancelled'
                      && scope.row.meeting_status !== 'completed'"
                  class="cancelButton"
                  icon="el-icon-delete"
                  type="text"
                  circle
                  @click="cancelMeeting(scope.row)"
                />
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="!['mobile', 'tablet'].includes($mq)"
          :label="$t('leader.coach.meetingStatus')"
          align="center"
          prop="meeting_status"
        >
          <template slot-scope="scope">
            <span
              v-if="scope.row.meeting_status"
              class="meetingStatus"
            >
              {{ $t(`meetingStatus.${scope.row.meeting_status}`) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column v-else />
      </el-table>

      <div
        v-if="has_other_engagements && !ignorePast && !next_meeting"
        class="viewOtherCoachesWrap"
      >
        <el-button
          class="viewOtherCoachesBtn"
          type="text"
          @click="viewMoreCoaches"
        >
          {{ $t('leader.coach.viewOthers') }}
        </el-button>
      </div>
    </div>

    <CoachInfo
      v-if="coaches.length > 0"
      :coach="coachData"
      :show.sync="showCoachInfo"
      @selectCoach="selectCoach"
    />
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from 'vuex';
import api from '@/api';
import CoachScheduler from '../scheduler/CoachScheduler.vue';
import CoachInfo from './CoachInfo.vue';
import CoachSelectionExplanation from './CoachSelectionExplanation.vue';

export default {
  components: {
    CoachScheduler,
    CoachInfo,
    CoachSelectionExplanation,
  },

  data() {
    return {
      loading: false,
      ignorePast: false,
      filterSelect: '',
      filterList: [],
      coaches: [],
      coachData: {},
      showCoachInfo: false,
      sessions: [
        {
          tasks: [
            {
              description: 'Select a coach who will be guiding you for the duration of your coaching engagement.',
              cta: 'route:coach',
            },
          ],
          media: [
            {
              type: 'document',
              name: 'How to choose your coach',
              link: '',
            },
            {
              thumbnail: '@/assets/media/media_thumb.png',
              type: 'video',
              name: 'How to run a introductory session',
              link: '',
            },
          ],
        },
      ],
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      next_meeting: (state) => state.processStatus.process.next_meeting,
      has_other_engagements: (state) => state.processStatus.process.has_other_engagements,
    }),

    filterOptions() {
      const options = [];

      this.filterList.forEach((item) => {
        options.push({
          value: item.key,
          label: item.value,
        });
      });

      options.sort((a, b) => {
        if (a.label > b.label) {
          return 1;
        }
        return -1;
      });

      return options;
    },

    // Responsive layout
    responsivePicture() {
      const col = {};
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.one = 24;
        col.two = 24;
      } else {
        col.one = 12;
        col.two = 12;
      }
      return col;
    },

    responsiveCol() {
      let col = 12;
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col = 24;
      }
      return col;
    },

    noDataText() {
      if (this.loading) return `${this.$t('leader.coach.loadingCoaches')}...`;

      const lang = this.filterList.find((el) => el.key === this.filterSelect);
      if (this.filterSelect === 'all' || this.filterSelect === '' || !lang) {
        return this.$t('leader.coach.fullyBooked');
      }
      return this.$t('leader.coach.tryAnotherLang', { lang: lang.value });
    },
  },

  watch: {
    has_other_engagements: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.getCoaches();
        }
      },
    },
  },

  created() {
    this.getFilter();
  },

  methods: {
    ...mapMutations('myCoach', [
      'setCoach',
    ]),

    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    async getFilter() {
      try {
        this.loading = true;
        const filter = await api.getCoachLanguages();
        this.filterList = filter.data;
        if (this.user.profile.language) {
          const matched = this.filterList.find((el) => el.value === this.user.profile.language);
          this.filterSelect = matched.key || 'en';
        } else {
          this.filterSelect = 'en';
        }

        this.getCoaches();
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
        this.loading = false;
      }
    },

    async getCoaches() {
      try {
        this.loading = true;
        const param = {};
        if (this.filterSelect && !(this.has_other_engagements && !this.ignorePast)) {
          param.lang = this.filterSelect;
        }
        const response = await api.getCoachList(param);
        if (this.has_other_engagements && !this.ignorePast) {
          this.coaches = response.data.filter((el) => el.is_featured);
          if (!this.coaches.length) {
            this.ignorePast = true;
            this.coaches = response.data;
          }
        } else {
          this.coaches = response.data;
        }
        this.loading = false;
      } catch (err) {
        console.log(err);
        // handle error in table empty text
        // if (err.msg) {
        //   this.$message.error(err.msg);
        // }
        this.loading = false;
      }
    },

    viewMoreCoaches() {
      this.ignorePast = true;
      this.getCoaches();
    },

    changePage(page) {
      this.currentPage = page;
      this.getCoaches();
    },

    openCoachInfo(coachData) {
      this.coachData = coachData;
      this.showCoachInfo = true;
    },

    async selectCoach(coach) {
      try {
        const response = await api.selectCoach(coach.uuid);
        const myCoach = response.data;

        this.setCoach({ coach: myCoach });
        this.fetchProcess();
      } catch (err) {
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    async cancelMeeting(param) {
      this.$msgbox({
        message: this.$t('leader.coach.confirmCancel', {
          coachName: `${param.first_name} ${param.last_name}`,
          date: `${this.$moment(param.meeting_schedule).format('YYYY-MM-DD HH:mm')}`,
        }),
        showClose: false,
        showCancelButton: true,
        confirmButtonText: this.$t('common.cancel'),
        cancelButtonText: this.$t('common.goBack'),
        roundButton: true,
        cancelButtonClass: 'el-button--info',
        customClass: 'coachMsgBox',
      }).then(async () => {
        try {
          const response = await api.cancelCoachMeeting(param.meeting_uuid);
          const index = this.coaches.findIndex((el) => el.email === response.data.coach);
          this.coaches[index].meeting_status = response.data.status;
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
        this.dialogVisible = false;
      }).catch(() => {});
    },

    handleCTA(action) {
      switch (action) {
        case 'selectCoach':
          window.Appcues.show('-Ll_CEEkaMafEDsccZpe');
          break;
        case 'introductorySession':
          this.$message('TODO');
          break;
        default:
          console.log('undefined action');
      }
    },

    // El-table layout method
    spanMethod(row) {
      let span = [];
      if (['mobile', 'tablet'].includes(this.$mq)) {
        if (row.columnIndex === 0) {
          span = [1, 1];
        } else if (row.columnIndex === 1) {
          span = [1, 2];
        } else if (row.columnIndex === 2) {
          span = [0, 0];
        }
      } else {
        span = [1, 1];
      }
      return span;
    },
  },
};
</script>

<style lang="scss">
.coachMsgBox {
  padding: 20px 10px 10px;

  .el-message-box__message {
    margin-bottom: 20px;
  }

  .el-message-box__btns {
    text-align: center;

    button {
      width: 48%;
    }
  }
}

#coachSelection {
  .intro {
    .el-card__body {
      padding: 25px 30px;
    }

    text-align: left;
    margin-bottom: 15px;

    .title,
    .text {
      margin-bottom: 15px;
    }

    .title {
      font-weight: bold;
    }

    .text {
      // Status - See more
      overflow: hidden;
      text-overflow: ellipsis;
      transition: all 450ms ease-in-out;

      // Status - See less
      &.short {
        max-height: 40px;
      }
    }

    .textSpan {
      margin-top: -10px;

      .spanBtn {
        padding: 0;
        color: var(--themeFontColor);
        text-decoration: underline;
      }
    }

    .media {
      display: flex;
      padding: 15px 30px;
      align-items: center;
      margin-top: 10px;
      margin-left: -30px;
      width: calc(100% + 60px);
      box-sizing: border-box;

      .thumbnail {
        width: 40px;
        position: relative;

        i {
          font-size: 30px;
        }
      }

      .mediaDesc {
        text-decoration: underline;
      }

      .mediaAction {
        margin-left: auto;
        display: none;
      }

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
        cursor: pointer;

        .mediaAction {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }

      &.mobile {
        flex-wrap: wrap;

        .thumbnail {
          width: 15%;
        }

        .mediaDesc {
          width: 80%;
          margin: 0;
        }

        .mediaAction {
          padding: 15px 0 0;
          width: 100%;
          text-align: center;
        }
      }
    }
  }

  .cta {
    .el-button {
      width: 100%;
      margin: 5px 0;
    }
  }

  .filter {
    max-width: 984px;
    margin: 0 auto 18px;
    text-align: right;

    .el-select {
      .el-input {
        input {
          border-radius: 20px;
          background-color: transparent;
          color: var(--themeFontColor);
        }
      }
    }
  }

  .coachList {
    position: relative;

    thead {
      th:first-child {
        .cell {
          padding-left: 14%;
        }
      }
    }

    .el-table__empty-block {
      padding: 25px;

      .el-table__empty-text {
        line-height: unset;
        white-space: pre-line;
      }
    }

    .coachRow {
      display: flex;
      align-items: center;
    }

    .pictureCol {
      display: flex;
      padding-left: 10%;

      .el-image {
        width: 70px;
        height: 70px;
        border-radius: 50%;
      }

      .image-slot {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.3);
        display: flex;
        justify-content: center;
        align-items: center;
        word-break: keep-all;
      }

      .imageAlt {
        width: 100%;
        height: 100%;

        i {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 35px;
          opacity: 0.7;
        }

        .defaultProfile {
          width: 100%;
          height: 100%;
        }
      }

      &.mobilePicture {
        .el-image {
          width: 55px;
          height: 55px;
        }
      }
    }

    .infoCol {
      .name {
        position: relative;
        top: 15px;
      }

      .infoLink {
        position: relative;
        height: 30px;
        bottom: 0;

        .el-button {
          color: var(--themeFontColor);
          text-decoration: underline;
          display: none;
        }

        &.mobileModeInfoLink {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;

          .el-button {
            display: inline-block;
            width: 100%;
            height: 100%;
            font-size: 0;
          }
        }
      }
    }

    .meetingStatus {
      font-style: italic;
      text-transform: capitalize;
    }

    .schedulerLayout {
      display: flex;

      .left {
        width: 90%;

        .mobileName {
          padding: 5px 8px;
          font-weight: bold;
        }

        .mobileStatus {
          padding: 5px 8px;
          text-align: left;
          font-style: italic;
          text-transform: capitalize;
        }
      }

      .right {
        width: 10%;
        display: flex;

        button {
          color: var(--themeFontColor);
        }

        .cancelButton {
          padding: 40%;
        }
      }
    }

    .el-table__row:hover {
      .infoCol {
        .infoLink {
          .el-button {
            display: inline;
          }
        }
      }
    }

    .mobileTable {
      thead {
        th:nth-child(2) {
          position: absolute;
          width: 100%;
        }
      }
    }
  }

  .viewOtherCoachesWrap {
    text-align: center;
    margin-top: 8px;

    .viewOtherCoachesBtn {
      color: var(--themeFontColor);
      text-decoration: underline;
    }
  }

  .el-pagination {
    margin-top: 20px;
    text-align: center;

    .btn-prev,
    .btn-next,
    .el-pager .number {
      background-color: transparent;
      color: var(--themeFontColor);

      &.active {
        color: #409eff;
      }
    }
  }
}
</style>
